import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { groupArchivesByMatchId, listLscArchive, listLscMatches, LscApiError } from '../api';
import type { LscArchiveItem, LscMatch } from '../types';
import MatchesTable from '../components/MatchesTable';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import PaginatedListFooter from '../components/PaginatedListFooter';
import { LEAGUE_SCHEDULES_PAGE_COPY } from '../leagueNav';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { summarizeLeagueScheduleMatches } from '../neonsportzCountUtils';
import { lscReturnTo } from '../routing';
import { displaySeasonLabel } from '../seasonDisplay';
import StreamGamesPage from '../components/stream/StreamGamesPage';
import { shouldRenderStreamPages } from '../stream/streamPages';
import {
  ALL_SEASONS_FILTER,
  ALL_TEAMS_FILTER,
  ALL_WEEKS_FILTER,
  defaultWeekFilter,
  extractSeasonLabels,
  extractTeamLabels,
  extractWeekLabels,
  filterMatchesBySearch,
  filterMatchesBySeason,
  filterMatchesByTeam,
  filterMatchesByWeek,
  sortLeagueScheduleMatches,
} from '../leagueScheduleSort';
const LscMatchesPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/matches`)}>
      <MatchesContent />
    </LscAuthGate>
  );
};

function MatchesContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const location = useLocation();
  const { league, isOwner, isCommissioner, isOperator } = useLeague();
  if (
    shouldRenderStreamPages({
      setting: league?.stream_pages,
      search: location.search,
      previewKey: 'view',
      viewer: { isOwner, isCommissioner },
    })
  ) {
    return (
      <StreamGamesPage
        leagueId={leagueId!}
        league={league ?? null}
        isOwner={isOwner}
        isCommissioner={isCommissioner}
        isOperator={isOperator}
      />
    );
  }
  return <CurrentMatchesContent />;
}

function CurrentMatchesContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { league } = useLeague();
  const [matches, setMatches] = useState<LscMatch[]>([]);
  const [archivesByMatchId, setArchivesByMatchId] = useState<Map<string, LscArchiveItem[]>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seasonFilter, setSeasonFilter] = useState<string>(ALL_SEASONS_FILTER);
  const [weekFilter, setWeekFilter] = useState<string>(ALL_WEEKS_FILTER);
  const [teamFilter, setTeamFilter] = useState<string>(ALL_TEAMS_FILTER);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const [matchData, archiveData] = await Promise.all([
          listLscMatches(leagueId, { scope: 'active' }),
          listLscArchive(leagueId, { archive_status: 'published' }).catch(() => []),
        ]);
        if (!cancelled) {
          setMatches(matchData);
          setArchivesByMatchId(groupArchivesByMatchId(archiveData));
          const activeSeason = (league?.active_season_label ?? '').trim();
          const activeWeek = (league?.active_stage_label ?? '').trim();
          setSeasonFilter(activeSeason || ALL_SEASONS_FILTER);
          setWeekFilter(activeWeek || ALL_WEEKS_FILTER);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof LscApiError ? err.message : LEAGUE_SCHEDULES_PAGE_COPY.loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, league?.active_season_label, league?.active_stage_label]);

  const seasonOptions = useMemo(() => extractSeasonLabels(matches), [matches]);
  const weekOptions = useMemo(
    () => extractWeekLabels(matches, seasonFilter),
    [matches, seasonFilter]
  );
  const teamOptions = useMemo(() => extractTeamLabels(matches), [matches]);

  const sortedMatches = useMemo(() => sortLeagueScheduleMatches(matches), [matches]);
  const visibleMatches = useMemo(() => {
    let rows = filterMatchesBySeason(sortedMatches, seasonFilter);
    rows = filterMatchesByWeek(rows, weekFilter);
    rows = filterMatchesByTeam(rows, teamFilter);
    rows = filterMatchesBySearch(rows, searchQuery);
    return rows;
  }, [sortedMatches, seasonFilter, weekFilter, teamFilter, searchQuery]);

  const pagination = usePaginatedList(visibleMatches);
  const scheduleSummary = useMemo(
    () => summarizeLeagueScheduleMatches(visibleMatches),
    [visibleMatches]
  );

  if (loading) return <LoadingState message="Loading games..." />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">{LEAGUE_SCHEDULES_PAGE_COPY.title}</h1>
        <p className="text-sm text-secondary-400">{LEAGUE_SCHEDULES_PAGE_COPY.subtitle}</p>
      </div>
      {error && <ErrorBanner message={error} />}

      <div
        className="grid max-w-lsc-narrow gap-3 md:grid-cols-2 xl:grid-cols-4 rounded-xl border border-secondary-700 bg-secondary-900/40 p-4"
        data-testid="league-games-filters"
      >
        <FilterSelect
          label={LEAGUE_SCHEDULES_PAGE_COPY.seasonFilterLabel}
          value={seasonFilter}
          options={[ALL_SEASONS_FILTER, ...seasonOptions]}
          onChange={(value) => {
            setSeasonFilter(value);
            const weeks = extractWeekLabels(matches, value);
            const preferredStage =
              value === (league?.active_season_label ?? '').trim()
                ? league?.active_stage_label
                : null;
            setWeekFilter(defaultWeekFilter(weeks, preferredStage));
            pagination.reset();
          }}
          testId="league-games-season-filter"
        />
        <FilterSelect
          label={LEAGUE_SCHEDULES_PAGE_COPY.weekFilterLabel}
          value={weekFilter}
          options={[ALL_WEEKS_FILTER, ...weekOptions]}
          onChange={(value) => {
            setWeekFilter(value);
            pagination.reset();
          }}
          testId="league-games-week-filter"
        />
        <FilterSelect
          label={LEAGUE_SCHEDULES_PAGE_COPY.teamFilterLabel}
          value={teamFilter}
          options={[ALL_TEAMS_FILTER, ...teamOptions]}
          onChange={(value) => {
            setTeamFilter(value);
            pagination.reset();
          }}
          testId="league-games-team-filter"
        />
        <label className="flex flex-col gap-1 text-sm md:col-span-2 xl:col-span-4">
          <span className="text-secondary-400">{LEAGUE_SCHEDULES_PAGE_COPY.searchLabel}</span>
          <input
            type="search"
            className="input-field"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              pagination.reset();
            }}
            placeholder="Search by team, week, or season"
            data-testid="league-games-search"
          />
        </label>
      </div>

      <div className="text-sm text-secondary-300" data-testid="league-games-summary">
        Showing {pagination.visibleCount} of {pagination.totalCount} game
        {pagination.totalCount === 1 ? '' : 's'}
        {scheduleSummary.seasonLabel ? (
          <>
            {' '}
            · <span className="text-secondary-100">{displaySeasonLabel(scheduleSummary.seasonLabel)}</span>
          </>
        ) : null}
      </div>

      {visibleMatches.length === 0 ? (
        <p className="text-secondary-400 text-sm">{LEAGUE_SCHEDULES_PAGE_COPY.emptyState}</p>
      ) : (
        <>
          <MatchesTable
            leagueId={leagueId!}
            matches={pagination.visibleItems}
            archivesByMatchId={archivesByMatchId}
            showMatchupBadges={league?.game_key === 'cfb'}
            activeEdition={league?.active_edition}
          />
          <PaginatedListFooter
            visibleCount={pagination.visibleCount}
            totalCount={pagination.totalCount}
            hasMore={pagination.hasMore}
            onShowMore={pagination.showMore}
          />
        </>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-secondary-400">{label}</span>
      <select className="input-field" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        {options.map((option) => (
          <option key={option} value={option}>
            {displaySeasonLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

export default LscMatchesPage;
